import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(6);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    setVisibleCount(6);
  }, [selectedCategory]);
  const featuredPost = {
    title: "The Future of AI in Business: 2024 and Beyond",
    excerpt: "Explore how artificial intelligence is reshaping industries and what it means for your business strategy in the coming years.",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=400&fit=crop",
    category: "AI & Technology",
    date: "Nov 25, 2024",
    readTime: "8 min read",
    slug: "future-of-ai-business",
  };

  const posts = [
    {
      title: "Building Scalable Cloud Architecture",
      excerpt: "Learn the best practices for designing cloud infrastructure that grows with your business.",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=250&fit=crop",
      category: "Cloud",
      date: "Nov 22, 2024",
      readTime: "6 min read",
      slug: "scalable-cloud-architecture",
    },
    {
      title: "Cybersecurity Trends for 2025",
      excerpt: "Stay ahead of threats with our comprehensive guide to emerging security challenges.",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=400&h=250&fit=crop",
      category: "Security",
      date: "Nov 20, 2024",
      readTime: "5 min read",
      slug: "cybersecurity-trends-2025",
    },
    {
      title: "React 19: What's New and Exciting",
      excerpt: "Dive into the latest features and improvements in the newest version of React.",
      image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop",
      category: "Development",
      date: "Nov 18, 2024",
      readTime: "7 min read",
      slug: "react-19-new-features",
    },
    {
      title: "Data-Driven Decision Making",
      excerpt: "How to leverage analytics to make smarter business decisions.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop",
      category: "Analytics",
      date: "Nov 15, 2024",
      readTime: "4 min read",
      slug: "data-driven-decisions",
    },
    {
      title: "Mobile App Development Best Practices",
      excerpt: "Essential guidelines for building high-performance mobile applications.",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=250&fit=crop",
      category: "Mobile",
      date: "Nov 12, 2024",
      readTime: "6 min read",
      slug: "mobile-development-practices",
    },
    {
      title: "The Rise of Edge Computing",
      excerpt: "Understanding how edge computing is transforming data processing.",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=250&fit=crop",
      category: "Infrastructure",
      date: "Nov 10, 2024",
      readTime: "5 min read",
      slug: "edge-computing-rise",
    },
  ];

  const categories = ["All", "AI & Technology", "Cloud", "Security", "Development", "Analytics", "Mobile"];

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-24 relative overflow-hidden px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto text-center relative z-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium mb-6 animate-fade-in">
              Our Blog
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6 animate-fade-in">
              Insights &
              <span className="gradient-text block">Innovation</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in">
              Stay updated with the latest trends, tutorials, and insights from 
              the world of technology and digital innovation.
            </p>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 border-b border-border px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  className={selectedCategory === category ? "glow-primary bg-gradient-to-r from-primary to-accent" : "glass"}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="relative group rounded-3xl overflow-hidden glass-card">
              <div className="grid md:grid-cols-2">
                <div className="aspect-video md:aspect-auto overflow-hidden">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="px-3 py-1 rounded-full bg-gradient-to-r from-primary to-accent text-white text-xs font-medium">
                      Featured
                    </span>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Tag className="w-3 h-3" />
                      {featuredPost.category}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 group-hover:text-primary transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-muted-foreground mb-6">{featuredPost.excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {featuredPost.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime}
                    </span>
                  </div>
                  <Button className="w-fit glow-primary bg-gradient-to-r from-primary to-accent hover:opacity-90" onClick={() => navigate(`/blog#${featuredPost.slug}`)}>
                    Read Article
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <h2 className="text-2xl font-heading font-bold mb-8">Latest Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
              {posts
                .filter((p) => selectedCategory === "All" || p.category === selectedCategory)
                .slice(0, visibleCount)
                .map((post, index) => (
                <article
                  key={post.slug}
                  className="group glass-card rounded-2xl overflow-hidden hover-lift cursor-pointer"
                  onClick={() => navigate(`/blog#${post.slug}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/blog#${post.slug}`)}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-heading font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            
            <div className="text-center mt-12">
              {(() => {
                const filtered = posts.filter((p) => selectedCategory === "All" || p.category === selectedCategory);
                const hasMore = visibleCount < filtered.length;
                return hasMore ? (
                  <Button variant="outline" size="lg" className="glass" onClick={() => setVisibleCount((c) => Math.min(c + 6, filtered.length))}>
                    Load More Articles
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : null;
              })()}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="max-w-2xl mx-auto glass-card rounded-3xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-heading font-bold mb-4">Never Miss an Update</h2>
              <p className="text-muted-foreground mb-8">
                Subscribe to our newsletter and get the latest articles delivered to your inbox.
              </p>
              <form
                className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
                onSubmit={(e) => {
                  e.preventDefault();
                  const input = e.currentTarget.querySelector('input[type="email"]');
                  if (input?.value) {
                    toast({ title: "Subscribed!", description: "You'll receive our latest articles and updates." });
                    input.value = "";
                  }
                }}
              >
                <input
                  type="email"
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                />
                <Button type="submit" className="glow-primary bg-gradient-to-r from-primary to-accent hover:opacity-90">
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
